import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { pageTitle } from '@/lib/page-title';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  ArrowUpRight, BookHeart, Box, Clapperboard, Coins, FolderOpen, Images, Megaphone, Music2, RefreshCw, Sparkles, Zap,
  type LucideIcon,
} from 'lucide-react';
import { Navbar } from '@/components/layout/Navbar';
import { Footer } from '@/components/layout/Footer';
import { StudioList } from '@/components/layout/StudioList';
import { useAuth } from '@/contexts/useAuth';
import { hasActiveRun } from '@/lib/active-run';
import { listSavedStorybooks } from '@/lib/storybook-api';
import { recentQuickJobs } from '@/lib/quicktools-api';
import { getProjects, markAlertsSeen, openBookPath, primeAlerts, type Project, type ProjectStudio } from '@/lib/projects-api';
import { REEL, savedRecipes } from '@/lib/reel';
import quickTools from '@/components/quicktools/tools.json';
import { trackPageView } from '@/lib/analytics';
import { cn } from '@/lib/utils';

// PROJECTS (Studio Blueprint 02/04, owner 2026-10-16): every piece of work
// this buyer has going, from every studio, in one list with live progress.
//
// Signed in, the account's own list comes from the server (routes/projects.js):
// music videos, story videos, ads, Animate this photo and storybooks, each
// with its state and a live bar while it renders (re-read every 15 s while
// anything is working). New "ready" alerts are marked "New" here and then
// counted as seen. Work the account can't know about (quick-tool files and
// storybooks made signed out, a render still running on this device) comes
// from this device, as before.

const STUDIO_ICON: Record<ProjectStudio, LucideIcon> = {
  music: Music2,
  music3d: Box,
  story: Clapperboard,
  ads: Megaphone,
  animate: Sparkles,
  storybook: BookHeart,
};

const LIVE_POLL_MS = 15_000;

interface DeviceRow {
  key: string;
  to: string;
  icon: LucideIcon;
  title: string;
  detail: string;
}

function StatePill({ state }: { state: Project['state'] }) {
  const { t } = useTranslation();
  return (
    <span
      className={cn(
        'inline-flex shrink-0 items-center gap-1.5 rounded-full px-2.5 py-1 text-[12px] font-semibold leading-none ring-1 ring-inset',
        state === 'working' && 'bg-accent/10 text-accent ring-accent/30',
        state === 'needs_you' && 'bg-white/[0.08] text-foreground ring-white/25',
        state === 'ready' && 'bg-accent/15 text-accent ring-accent/40',
        state === 'failed' && 'bg-destructive/10 text-destructive ring-destructive/30',
        state === 'cancelled' && 'bg-white/[0.04] text-muted-foreground ring-white/10',
      )}
      data-testid={`project-state-${state}`}
    >
      <span
        aria-hidden
        className={cn(
          'h-1.5 w-1.5 rounded-full',
          state === 'working' && 'animate-pulse bg-accent motion-reduce:animate-none',
          state === 'needs_you' && 'bg-foreground',
          state === 'ready' && 'bg-accent',
          state === 'failed' && 'bg-destructive',
          state === 'cancelled' && 'bg-muted-foreground',
        )}
      />
      {t(`projects.state.${state}`)}
    </span>
  );
}

export default function Projects() {
  const { t, i18n } = useTranslation();
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();

  const [account, setAccount] = useState<Project[] | null>(null);
  const [fresh, setFresh] = useState<Set<string>>(new Set());
  const [loadFailed, setLoadFailed] = useState(false);
  const [opening, setOpening] = useState<string | null>(null);
  const seenOnce = useRef(false);

  useEffect(() => {
    trackPageView('projects', { signedIn: isAuthenticated });
  }, [isAuthenticated]);
  useEffect(() => {
    const previous = document.title;
    document.title = pageTitle(`${t('projects.title')} | FaceShot-ChopShop`);
    return () => {
      document.title = previous;
    };
  }, [t]);

  const load = useCallback(async () => {
    try {
      const data = await getProjects();
      setAccount(data.projects);
      setLoadFailed(false);
      if (data.alerts.length) {
        setFresh((prev) => new Set([...prev, ...data.alerts.map((a) => `${a.ref_kind === 'book' ? 'book' : 'job'}:${a.ref_id}`)]));
        primeAlerts([]);
        // Seen on this page: the frame stops announcing them.
        void markAlertsSeen(data.alerts.map((a) => a.id));
      } else if (!seenOnce.current) {
        primeAlerts([]);
      }
      seenOnce.current = true;
    } catch {
      setLoadFailed(true);
    }
  }, []);

  useEffect(() => {
    if (!isAuthenticated) {
      setAccount(null);
      return;
    }
    void load();
  }, [isAuthenticated, load]);

  // A live bar: re-read while anything is still working and the tab is in view.
  const anyWorking = !!account?.some((p) => p.state === 'working');
  useEffect(() => {
    if (!isAuthenticated || !anyWorking) return undefined;
    const id = setInterval(() => {
      if (document.visibilityState === 'visible') void load();
    }, LIVE_POLL_MS);
    return () => clearInterval(id);
  }, [isAuthenticated, anyWorking, load]);

  const when = useCallback(
    (iso: string | number | null) => {
      if (!iso) return '';
      const ms = typeof iso === 'number' ? iso : Date.parse(iso);
      return Number.isFinite(ms)
        ? new Date(ms).toLocaleString(i18n.language, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })
        : '';
    },
    [i18n.language],
  );

  const accountBookIds = useMemo(() => new Set((account || []).filter((p) => p.kind === 'book').map((p) => p.id)), [account]);

  const deviceRows = useMemo<DeviceRow[]>(() => {
    const out: DeviceRow[] = [];
    // Signed in, a running music video is already on the account list.
    if (!isAuthenticated && hasActiveRun()) {
      out.push({ key: 'corrido', to: '/music#director-console', icon: Clapperboard, title: t('projects.musicRunning'), detail: t('projects.musicRunningDetail') });
    }
    for (const book of listSavedStorybooks()) {
      if (accountBookIds.has(book.id)) continue;
      out.push({ key: `book-${book.id}`, to: `/storybook/${book.id}`, icon: BookHeart, title: t('projects.book'), detail: when(book.createdAt || null) });
    }
    for (const job of recentQuickJobs()) {
      const spec = quickTools.tools[job.tool];
      if (!spec) continue;
      out.push({ key: `qt-${job.id}`, to: `${spec.path}/${job.id}`, icon: Zap, title: t(`qt.tools.${job.tool}.name`), detail: t('projects.quickDetail', { when: when(job.at) }) });
    }
    // Recipes saved from the Reel (Save on /watch).
    for (const id of savedRecipes()) {
      const item = REEL.find((r) => r.id === id);
      if (item) out.push({ key: `recipe-${id}`, to: `/watch?item=${id}`, icon: Clapperboard, title: t(`reel.items.${item.key}`), detail: t('projects.savedRecipe') });
    }
    return out;
  }, [t, when, isAuthenticated, accountBookIds]);

  const titleOf = (p: Project) => {
    if (p.studio === 'storybook' && p.detail.name) return t('projects.storybookOf', { name: p.detail.name });
    if (p.studio === 'ads' && p.detail.seconds) return t('projects.adSeconds', { seconds: p.detail.seconds });
    return t(`projects.studio.${p.studio}`);
  };

  const lineOf = (p: Project) => {
    if (p.state === 'working') {
      const base = t(`projects.working.${p.stage}`, { defaultValue: t('projects.working.default') });
      return p.progress != null && p.progress > 0 ? `${base} · ${p.progress}%` : base;
    }
    if (p.state === 'needs_you') return t(`projects.needsYou.${p.stage}`, { defaultValue: t('projects.needsYou.default') });
    if (p.state === 'ready') return t(`projects.ready.${p.stage}`, { defaultValue: t('projects.ready.default') });
    if (p.state === 'failed') return t('projects.failedLine');
    return t('projects.cancelledLine');
  };

  const openProject = async (p: Project) => {
    if (typeof p.open === 'string') {
      navigate(p.open);
      return;
    }
    setOpening(p.id);
    try {
      navigate(await openBookPath(p.open.book));
    } catch {
      setLoadFailed(true);
    } finally {
      setOpening(null);
    }
  };

  const nothing = (account?.length || 0) + deviceRows.length === 0;

  return (
    <div className="min-h-screen overflow-x-clip bg-background">
      <Navbar />
      <main className="container mx-auto max-w-3xl px-4 pb-16 pt-24">
        <p className="eyebrow">{t('shell.tabs.projects')}</p>
        <h1 className="mt-2 font-display text-[30px] font-bold leading-tight sm:text-[40px]">{t('projects.title')}</h1>
        <p className="mt-2 text-muted-foreground">{t('projects.lead')}</p>
        {isAuthenticated && <p className="mt-1 text-[14px] text-muted-foreground" data-testid="projects-alerts-line">{t('projects.alertsLine')}</p>}

        {isAuthenticated && account === null && !loadFailed && (
          <ul className="mt-6 space-y-2" aria-hidden>
            {[0, 1].map((i) => (
              <li key={i} className="h-[92px] animate-pulse rounded-2xl border border-white/10 bg-white/[0.03] motion-reduce:animate-none" />
            ))}
          </ul>
        )}

        {loadFailed && (
          <div className="mt-6 flex items-center justify-between gap-3 rounded-2xl border border-white/10 bg-white/[0.03] p-4" data-testid="projects-load-failed">
            <p className="text-[15px] text-muted-foreground">{t('projects.loadFailed')}</p>
            <button
              type="button"
              onClick={() => void load()}
              className="inline-flex min-h-[44px] shrink-0 items-center gap-2 rounded-full border border-white/15 bg-white/[0.06] px-5 font-semibold hover:border-primary/50 hover:bg-primary/10"
            >
              <RefreshCw aria-hidden className="h-4 w-4" /> {t('projects.retry')}
            </button>
          </div>
        )}

        {account && account.length > 0 && (
          <ul className="mt-6 space-y-2" data-testid="projects-account-list">
            {account.map((p) => {
              const Icon = STUDIO_ICON[p.studio] || FolderOpen;
              const isNew = fresh.has(`${p.kind}:${p.id}`);
              return (
                <li key={`${p.kind}-${p.id}`}>
                  <div
                    className={cn(
                      'rounded-2xl border bg-white/[0.03] p-4 transition-colors',
                      isNew ? 'border-accent/50 bg-accent/[0.05]' : 'border-white/10',
                    )}
                    data-testid="project-row"
                  >
                    <div className="flex items-start gap-3">
                      <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-primary/[0.12] text-primary ring-1 ring-inset ring-primary/25">
                        <Icon aria-hidden className="h-5 w-5" />
                      </span>
                      <div className="min-w-0 flex-1">
                        <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                          <span className="font-semibold text-foreground">{titleOf(p)}</span>
                          {isNew && (
                            <span className="rounded-full bg-accent px-2 py-0.5 text-[11px] font-bold uppercase tracking-wide text-[#081014]">
                              {t('projects.new')}
                            </span>
                          )}
                        </div>
                        <p className="mt-0.5 text-[14px] text-muted-foreground">{lineOf(p)}</p>
                        <p className="mt-0.5 text-[12px] tabular-nums text-muted-foreground/80">{t('projects.updated', { when: when(p.updated_at) })}</p>
                      </div>
                      <StatePill state={p.state} />
                    </div>
                    {p.state === 'working' && (
                      <div
                        className="mt-3 h-1.5 overflow-hidden rounded-full bg-white/10"
                        role="progressbar"
                        aria-valuemin={0}
                        aria-valuemax={100}
                        aria-valuenow={p.progress ?? undefined}
                        aria-label={lineOf(p)}
                      >
                        <div
                          className={cn('h-full rounded-full bg-accent transition-[width] duration-700', p.progress == null && 'w-1/3 animate-pulse motion-reduce:animate-none')}
                          style={p.progress != null ? { width: `${Math.max(4, p.progress)}%` } : undefined}
                        />
                      </div>
                    )}
                    <div className="mt-3 flex justify-end">
                      <button
                        type="button"
                        onClick={() => void openProject(p)}
                        disabled={opening === p.id}
                        data-testid="project-open"
                        className="inline-flex min-h-[44px] items-center gap-1.5 rounded-full border border-white/15 bg-white/[0.06] px-5 font-semibold hover:border-primary/50 hover:bg-primary/10 disabled:opacity-60"
                      >
                        {t('projects.open')} <ArrowUpRight aria-hidden className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
        )}

        {deviceRows.length > 0 && (
          <>
            {isAuthenticated && <h2 className="mt-8 font-display text-[18px] font-bold">{t('projects.deviceTitle')}</h2>}
            <ul className={cn('space-y-2', isAuthenticated ? 'mt-3' : 'mt-6')} data-testid="projects-list">
              {deviceRows.map(({ key, to, icon: Icon, title, detail }) => (
                <li key={key}>
                  <Link to={to} className="group flex min-h-[64px] items-center gap-3 rounded-2xl border border-white/10 bg-white/[0.03] p-4 hover:border-primary/40">
                    <Icon aria-hidden className="h-5 w-5 shrink-0 text-primary" />
                    <span className="min-w-0 flex-1">
                      <span className="block font-semibold text-foreground">{title}</span>
                      <span className="block truncate text-[13px] text-muted-foreground">{detail}</span>
                    </span>
                    <span className="flex items-center gap-1 text-[14px] font-semibold text-muted-foreground group-hover:text-primary">
                      {t('projects.open')} <ArrowUpRight aria-hidden className="h-4 w-4" />
                    </span>
                  </Link>
                </li>
              ))}
            </ul>
          </>
        )}

        {nothing && (!isAuthenticated || (account !== null && !loadFailed)) && (
          <p className="mt-6 rounded-2xl border border-white/10 bg-white/[0.03] p-5 text-muted-foreground" data-testid="projects-empty">
            {isAuthenticated ? t('projects.emptyAccount') : t('projects.empty')}
          </p>
        )}

        {isAuthenticated ? (
          <div className="mt-6 grid gap-2 sm:grid-cols-2">
            <Link to="/chopshop#my-gallery" className="flex min-h-[56px] items-center gap-3 rounded-2xl border border-white/10 bg-white/[0.03] px-4 font-semibold hover:border-primary/40">
              <Images aria-hidden className="h-5 w-5 text-accent" /> {t('projects.gallery')}
            </Link>
            <Link to="/credits-history" className="flex min-h-[56px] items-center gap-3 rounded-2xl border border-white/10 bg-white/[0.03] px-4 font-semibold hover:border-primary/40">
              <Coins aria-hidden className="h-5 w-5 text-primary" /> {t('projects.credits')}
            </Link>
          </div>
        ) : (
          <p className="mt-6 text-[15px] text-muted-foreground">
            {t('projects.signInLead')}{' '}
            <Link to="/login?redirect_to=%2Fprojects" className="font-semibold text-primary underline-offset-4 hover:underline">
              {t('auth.login')}
            </Link>
          </p>
        )}

        <h2 className="mt-12 font-display text-[22px] font-bold">{t('projects.startTitle')}</h2>
        <StudioList className="mt-4" />
      </main>
      <Footer />
    </div>
  );
}
